import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";

import { capitalizeString } from "@excalidraw/common";

import { Popover } from "radix-ui";

import { trackEvent } from "../analytics";

import { ToolButton } from "./ToolButton";

import "./ToolPopover.scss";

import { useExcalidrawContainer } from "./App";

import type { AppClassProperties } from "../types";

type ToolOption = {
  type: string;
  customType?: string | null;
  icon: React.ReactNode;
  title?: string;
  onChange?: (app: AppClassProperties, type: string) => void;
};

type ToolPopoverProps = {
  app: AppClassProperties;
  options: readonly ToolOption[];
  activeTool: { type: string };
  defaultOption: string;
  className?: string;
  namePrefix: string;
  title: string;
  "data-testid": string;
  onToolChange: (type: string) => void;
  displayedOption: ToolOption;
  fillable?: boolean;
  selectOnOpen?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
};

export const ToolPopover = ({
  app,
  options,
  activeTool,
  defaultOption,
  className = "Shape",
  namePrefix,
  title,
  "data-testid": dataTestId,
  onToolChange,
  displayedOption,
  fillable = false,
  selectOnOpen = true,
  side = "bottom",
  align = "center",
}: ToolPopoverProps) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const currentType = activeTool.type;
  const currentCustomType = (activeTool as any).customType;
  const isActive =
    displayedOption.type === currentType &&
    (!displayedOption.customType ||
      displayedOption.customType === currentCustomType);
  const SIDE_OFFSET = 32 / 2 + 10;
  const { container } = useExcalidrawContainer();
  const triggerRef = useRef<HTMLDivElement>(null);

  const previousActiveToolRef = useRef(activeTool.type);
  useEffect(() => {
    if (
      isPopupOpen &&
      (previousActiveToolRef.current !== activeTool.type ||
        (activeTool as any).customType) &&
      !options.some(
        (o) =>
          o.type === activeTool.type &&
          (!o.customType || o.customType === (activeTool as any).customType),
      )
    ) {
      setIsPopupOpen(false);
    }
    previousActiveToolRef.current = activeTool.type;
  }, [activeTool, isPopupOpen, options]);

  // Close popover when user starts interacting with the canvas (pointer down)
  useEffect(() => {
    // app.onPointerDownEmitter emits when pointer down happens on canvas area
    const unsubscribe = app.onPointerDownEmitter.on(() => {
      setIsPopupOpen(false);
    });
    return () => unsubscribe?.();
  }, [app]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!isPopupOpen) {
        return;
      }
      const target = event.target as Node;
      const isInsideTrigger = triggerRef.current?.contains(target);
      const isInsideContent = document
        .querySelector(".tool-popover-content")
        ?.contains(target);

      if (!isInsideTrigger && !isInsideContent) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, {
      capture: true,
    });
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
  }, [isPopupOpen]);

  return (
    <Popover.Root open={isPopupOpen}>
      <div ref={triggerRef} style={{ display: "inline-flex" }}>
        <Popover.Trigger asChild>
          <ToolButton
            className={clsx(className, {
              fillable,
              active: options.some(
                (o) =>
                  o.type === activeTool.type &&
                  (!o.customType ||
                    o.customType === (activeTool as any).customType),
              ),
            })}
            type="radio"
            icon={displayedOption.icon}
            checked={isActive}
            name="editor-current-shape"
            title={title}
            aria-label={title}
            data-testid={dataTestId}
            onPointerDown={() => {
              setIsPopupOpen((v) => !v);
              if (selectOnOpen) {
                onToolChange(defaultOption);
              }
            }}
          />
        </Popover.Trigger>
      </div>

      <Popover.Portal container={container}>
        <Popover.Content
          className="tool-popover-content"
          side={side}
          align={align}
          sideOffset={SIDE_OFFSET}
          collisionBoundary={container ?? undefined}
        >
          {options.map((option) => {
            const { type, customType, icon, title } = option;
            const isOptionActive =
              currentType === type &&
              (!customType || customType === currentCustomType);
            return (
              <ToolButton
                className={clsx(className, {
                  active: isOptionActive,
                })}
                key={customType || type}
                type="radio"
                icon={icon}
                checked={isOptionActive}
                name={`${namePrefix}-option`}
                title={title || capitalizeString(type)}
                keyBindingLabel=""
                aria-label={title || capitalizeString(type)}
                data-testid={`toolbar-${customType || type}`}
                onChange={() => {
                  if (
                    app.state.activeTool.type !== type ||
                    (activeTool as any).customType !== customType
                  ) {
                    trackEvent("toolbar", customType || type, "ui");
                  }
                  if (option.onChange) {
                    option.onChange(app, type);
                  } else if (customType) {
                    app.setActiveTool({ type: "custom", customType });
                  } else {
                    app.setActiveTool({ type: type as any });
                  }
                  onToolChange?.(customType || type);
                  setIsPopupOpen(false);
                }}
              />
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
